import { useUpflowyStore, useUpflowyStoreRefs } from '@upflowy/firestore';
import { logger } from '@upflowy/logger';
import { FlowAssetDoc, FlowVersionDoc, helper, ThemeVersionDoc } from '@upflowy/types';
import { doc, DocumentReference } from 'firebase/firestore';
import { useEffect, useMemo, useRef, useState } from 'react';

import { StateView } from './StateView';

import { useFlowVersion } from '../contexts/Version';
import { fetchDoc } from '../helpers/fetch-documents';
import { getMetaData } from '../helpers/meta-data';
import { useRedirect } from '../hooks/useRedirect';
import { ILocation, useApp } from '../providers/app-provider';
import { useFlowConfig } from '../providers/flow-config-provider';
import { useNetworkErrorHandler } from '../providers/network-error-handler-provider';
import { StateMachineProvider } from '../providers/state-machine-provider';
import AssetRegistry from '../services/asset-registry';
import { XStateMachineConfig } from '../types';

const generateFontCSS = (fontName?: string, fontUrl?: string) => `@font-face {
    font-family: ${fontName};
    src: url(${fontUrl});
  }`;

function Version({
  user,
  flowId,
  version,
  initialState,
  initialContext,
  assets,
}: {
  user: { idToken: string; location: ILocation };
  flowId: string;
  version: FlowVersionDoc;
  initialState: string;
  initialContext: Record<string, unknown>;
  assets: FlowAssetDoc[];
}) {
  const divRef = useRef<HTMLDivElement>(null);
  const assetsRegistryRef = useRef<AssetRegistry>();
  const firestore = useUpflowyStore();
  const { flows } = useUpflowyStoreRefs();
  const [themeVersion, setThemeVersion] = useState<ThemeVersionDoc>();
  const [networkError, setNetworkError] = useState(false);
  const { dependenciesLoaded, setDependenciesLoaded } = useFlowVersion();
  const { networkErrorHandler, retryCount } = useNetworkErrorHandler();
  const handleRedirect = useRedirect();

  const retrievedThemeVersion = useMemo(() => themeVersion?.id, [themeVersion]);

  const flowRef = flows().id(flowId).versions().id(version.id);
  const stepsRef = flowRef.steps();
  const primaryFontName = version.meta?.options
    ? version.meta?.options['primary-font-family']
    : undefined;
  const secondaryFontName = version.meta?.options
    ? version.meta?.options['secondary-font-family']
    : undefined;

  useEffect(() => {
    if (!version.meta?.themeVersionRef) return;
    const themeVersionRef = doc(
      firestore,
      version.meta?.themeVersionRef,
    ) as DocumentReference<ThemeVersionDoc>;
    fetchDoc(themeVersionRef)
      .then((t) => setThemeVersion(t.data()))
      .catch((err) => {
        networkErrorHandler(err, () => {
          setNetworkError(!retrievedThemeVersion);
        });
      });
  }, [
    firestore,
    version.meta?.themeVersionRef,
    retryCount,
    networkErrorHandler,
    retrievedThemeVersion,
  ]);

  useEffect(() => {
    if (!divRef.current) return;

    if (!assetsRegistryRef.current) {
      assetsRegistryRef.current = new AssetRegistry(divRef.current);
    }

    const priorityAssetsCount = assets.reduce((accumulator, asset: FlowAssetDoc) => {
      if (asset.priority === 1 || asset.name === 'gjs-base-components') return accumulator + 1;

      return accumulator;
    }, 0);

    if (priorityAssetsCount === 0) setDependenciesLoaded && setDependenciesLoaded(true);

    assets.forEach((asset) => {
      try {
        assetsRegistryRef.current?.insertNewAsset(
          asset,
          priorityAssetsCount,
          setDependenciesLoaded,
        );
      } catch (e) {
        logger.warn(e.message, { flowId, versionId: version.id, e });
      }
    });
  }, [assets, flowId, initialContext, setDependenciesLoaded, version.id, dependenciesLoaded]);

  const cssVars = useMemo(
    () =>
      Object.entries(helper.generateCSSKeys(version.meta?.options) ?? {}).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [`--${key}`]: value,
        }),
        {},
      ),
    [version],
  );
  const generateLink = (fileUrl: string) => {
    const link = document.createElement('link');
    link.type = 'text/css';
    link.rel = 'stylesheet';
    link.href = fileUrl;
    return link;
  };
  useEffect(() => {
    if (version.meta.options?.font) {
      const { head } = document;
      const link = generateLink(version.meta.options?.font);
      head.appendChild(link);
      return () => {
        head.removeChild(link);
      };
    }
    return () => null;
  }, [version.meta?.options?.font]);

  useEffect(() => {
    if (
      version.meta.options?.secondaryFont &&
      version.meta?.options?.secondaryFont !== version.meta?.options?.Font
    ) {
      const { head } = document;
      const link = generateLink(version.meta.options?.secondaryFont);
      head.appendChild(link);
      return () => {
        head.removeChild(link);
      };
    }
    return () => null;
  }, [version.meta?.options?.Font, version.meta?.options?.secondaryFont]);

  useEffect(() => {
    if (networkError && !window.location.pathname.startsWith('/offline')) {
      handleRedirect(`/offline?returnTo=${window.location.pathname}`);
    }
  }, [networkError, handleRedirect]);

  useEffect(() => {
    if (networkError) {
      handleRedirect(`/offline?returnTo=${window.location.pathname}`);
    }
  }, [networkError, handleRedirect]);

  useEffect(() => {
    if (networkError) {
      handleRedirect(`/offline?returnTo=${window.location.pathname}`);
    }
  }, [networkError, handleRedirect]);

  return (
    <div className="App" style={cssVars} ref={divRef}>
      {themeVersion?.css && <style>{themeVersion.css}</style>}
      <style>
        {version.meta.options?.font && generateFontCSS(primaryFontName, version.meta.options?.font)}
        {version.meta.options?.secondaryFont &&
          generateFontCSS(secondaryFontName, version.meta.options?.secondaryFont)}
      </style>
      {user && (
        <>
          <StateMachineProvider
            flowId={flowId}
            flowVersionId={version.id}
            machineConfig={(version.machine ?? {}) as XStateMachineConfig}
            initialState={initialState}
            initialContext={initialContext}
            userIdToken={user.idToken}
          >
            <StateView stepsRef={stepsRef} />
          </StateMachineProvider>
        </>
      )}
    </div>
  );
}

const VersionPrerequisities = () => {
  const { flowId } = useFlowConfig();
  const { version, flowVersionAssets } = useFlowVersion();
  const { urlParams, utmParams, user } = useApp();
  if (!user || !flowId || !version) {
    return <></>;
  }
  const initialContext = {
    ...Object.entries(urlParams).reduce(
      (p, [key, value]) => ({
        ...p,
        [key === 'initial' ? 'initial' : `user.${key}`]: value,
      }),
      {},
    ),
    ...Object.entries(utmParams).reduce(
      (p, [key, value]) => ({
        ...p,
        [`user.data.${key}`]: value,
        [`meta.utm.${key}`]: value,
      }),
      {},
    ),
    ...getMetaData({ location: user.location }),
  };

  return (
    <Version
      user={user}
      flowId={flowId}
      version={version}
      initialState={urlParams.initial}
      initialContext={initialContext}
      assets={flowVersionAssets}
    />
  );
};
export default VersionPrerequisities;
